/**
 * DEV-ONLY pricing-cache bypass.
 *
 * Until the v2 pricing functions are deployed, production Firestore holds
 * only old-pipeline docs, so no real flow on localhost can show corrected
 * prices. `functions/scripts/generate-dev-pricing-overrides.js` computes
 * real v2 docs for a test set of pedals (read-only, live Reverb fetches,
 * zero Firestore writes) into `public/dev-pricing-overrides.json`; this
 * module overlays them by pedalPricing doc key so the ACTUAL listing page,
 * browse thumbnails, Add Pedal flow, and offer surfaces render v2 pricing
 * during local verification.
 *
 * Production safety, twice over: the fetch below is inside a compile-time
 * NODE_ENV check (dead-code-eliminated from production bundles), and
 * firebase.json hosting.ignore excludes the JSON from ever deploying.
 */

let overridesPromise = null;

/**
 * Resolve one doc key against the overlay with explicit coverage semantics:
 *   - undefined  → overlay absent or non-exclusive miss: caller proceeds with
 *                  its normal production path (Firestore / callable).
 *   - a doc      → override hit: caller uses it as THE pricing doc.
 *   - null       → EXCLUSIVE overlay miss: caller treats the pedal as having
 *                  no pricing data. Nothing silently falls back to old
 *                  production pricing, and the on-demand callable (which
 *                  writes production pedalPricing docs under the old
 *                  pipeline) is never invoked during the preview.
 * Exclusive mode is declared by the generator via `__meta.exclusive` in the
 * JSON; doc keys can never start with "_" (normalizeIdentityPart strips leading
 * underscores) so the meta key cannot collide.
 */
export function resolveDevOverride(overrides, docKey) {
  if (!overrides) return undefined;
  if (docKey && overrides[docKey]) return overrides[docKey];
  if (overrides.__meta && overrides.__meta.exclusive) return null;
  return undefined;
}

export function getDevPricingOverrides() {
  if (process.env.NODE_ENV !== 'development') return Promise.resolve(null);
  if (!overridesPromise) {
    // Cache-busting query: the generator rewrites this file while the dev
    // server runs, and a browser HTTP-cached copy would silently pin the
    // preview to an earlier, partial dataset. Each page load fetches the
    // file as it exists NOW; the console line below states which snapshot
    // the session is actually using.
    overridesPromise = fetch(`/dev-pricing-overrides.json?v=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.__meta) {
          // eslint-disable-next-line no-console
          console.info(
            `[dev pricing overlay] ${data.__meta.pedalCount} docs, generated ${data.__meta.generatedAt}, exclusive=${data.__meta.exclusive === true}`
          );
        }
        // Never cache a miss: if the JSON was not there (or not yet
        // generated) on first load, retry on the next call instead of
        // latching "no overrides" for the whole session.
        if (!data) overridesPromise = null;
        return data;
      })
      .catch(() => { overridesPromise = null; return null; });
  }
  return overridesPromise;
}
